import axiosInstance from './AxiosInterceptor';
import { SystemTablesResponse } from '../models/SystemTables';
import { UserOrdersResponse } from '../models/UserOrdersResponse';
import { GetUnderlingUserOrdersParams } from '../models/GetUnderlingUserOrdersParams';

/**
 * Login API call
 * @param userName - The username of the user
 * @param password - The user's password
 * @param newPassword - Optional new password for NEW_PASSWORD_REQUIRED challenge
 */
export const login = async (userName: string, password: string, newPassword?: string) => {
  const response = await axiosInstance.post(`/auth/login`, {
    userName,
    password,
    newPassword,
  });
  return response.data;
};

/**
 * Logout functionality to clear tokens
 * only used in AxiosInterceptor in the components using AuthContext
 */
export const logout = () => {
  // Clear the tokens from localStorage (or any other storage method)
  localStorage.removeItem('idToken');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('userName');

  // Redirect to the login page (full page reload)
  window.location.href = '/'; 
};

/**
 * forgot password API call
 * @param userName - The username of the user
 */
export const forgotPassword = async (userName: string) => {
  const action = 'FORGOT_PASSWORD';
  const response = await axiosInstance.post(`/auth/login`, { 
    userName,
    action      
  });
  return response.data;
};

/**
 * resend confirmation code API call
 * @param userName 
 */
export const resendConfirmationCode = async (userName: string) => {
  const action = 'RESEND_CONFIRMATION_CODE';
  const response = await axiosInstance.post(`/auth/login`, { 
    userName,
    action
  });
  return response.data;
};

export const confirmResetRequest = async (userName: string, confirmationCode: string, newPassword: string) => {
  const action = 'CONFIRM_FORGOT_PASSWORD'
  const response = await axiosInstance.post(`/auth/login`, {
    userName,
    confirmationCode,
    newPassword,
    action
  });
  return response.data;
};

/**
 * Fetch system tables for the logged-in user.
 */
export const getSystemTables = async (): Promise<SystemTablesResponse> => {
  const response = await axiosInstance.get<SystemTablesResponse>('/system/tables');
  return response.data;
};

/**
 * Fetch underlings orders for the logged-in user.
 */
export const getUnderlingUserOrders = async (params?: GetUnderlingUserOrdersParams): Promise<UserOrdersResponse> => {
  // build a `query` object for the GET request
  const queryParams: Record<string, string | number | undefined> = {
    clientInnerId: params?.clientInnerId,
    startDate: params?.startDate,
    endDate: params?.endDate,
    orderId: params?.orderId,
    status: params?.status,
    origin: params?.origin,
    isSelfShipping: params?.isSelfShipping,
    transportationCompany: params?.transportationCompany,
    page: params?.page,
    limit: params?.limit,
  };

  // Filter out undefined keys
  Object.keys(queryParams).forEach((key) => {
    if (queryParams[key] === undefined) {
      delete queryParams[key];
    }
  });

  const response = await axiosInstance.get<UserOrdersResponse>('/orders', {
    params: queryParams,
  });
  const data = response.data;
  data.orders.forEach((o) => {
    if (o.orderCreationTime) {
      o.orderCreationTime = parseDate(o.orderCreationTime);
    }
    if (o.requestedTime) {
      o.requestedTime = parseDate(o.requestedTime);
    }
    if (o.expectedReceiveTime) {
      o.expectedReceiveTime = parseDate(o.expectedReceiveTime);
    }
  });

  return data;
};

/** 
 * Utility that tries to parse a string into a Date, 
 * returning undefined if invalid or empty.
 */
function parseDate(dateString: string | Date): Date | undefined {
  if (!dateString || typeof dateString !== 'string') return undefined;
  const d = new Date(dateString);
  return isNaN(d.getTime()) ? undefined : d;
}

export const createOrder = async (orderData: {
  itemNum: string;
  origin: string;
  amount: string | number;
  isSelfShipping: boolean;
  requestedTime: string;
  transportationCompany?: string;
  underlingUserName?: string;
  tonAmount?: string | number;
  comment?: string;
}) => {
  // Convert string values to numbers for amount and tonAmount
  const processedOrderData = {
    ...orderData,
    amount: typeof orderData.amount === 'string' ? parseFloat(orderData.amount) || 0 : orderData.amount,
    tonAmount: orderData.tonAmount ? (typeof orderData.tonAmount === 'string' ? parseFloat(orderData.tonAmount) || 0 : orderData.tonAmount) : undefined,
  };

  // POST to the correct endpoint for creating orders
  const response = await axiosInstance.post(`/orders`, processedOrderData);
  return response.data;
};

export const updateOrder = async (orderData: {
  orderId: string;
  itemNum: string;
  clientInnerId: string;
  origin?: string | number;
  amount?: string | number;
  isSelfShipping?: boolean;
  requestedTime?: string;
  transportationCompany?: string;
  tonAmount?: string | number;
  comment?: string;
}) => {
  // Convert string values to numbers for amount and tonAmount
  // Convert origin to number if it's a string
  const processedOrderData: any = {
    orderId: orderData.orderId,
    itemNum: orderData.itemNum,
    clientInnerId: orderData.clientInnerId,
  };

  // Add optional fields if they exist
  if (orderData.origin !== undefined) {
    processedOrderData.origin = typeof orderData.origin === 'string' ? parseInt(orderData.origin, 10) : orderData.origin;
  }
  if (orderData.amount !== undefined) {
    processedOrderData.amount = typeof orderData.amount === 'string' ? parseFloat(orderData.amount) || 0 : orderData.amount;
  }
  if (orderData.isSelfShipping !== undefined) {
    processedOrderData.isSelfShipping = orderData.isSelfShipping;
  }
  if (orderData.requestedTime) {
    // Convert datetime-local format to ISO string
    const date = new Date(orderData.requestedTime);
    processedOrderData.requestedTime = date.toISOString();
  }
  if (orderData.transportationCompany !== undefined) {
    processedOrderData.transportationCompany = orderData.transportationCompany;
  }
  if (orderData.tonAmount !== undefined) {
    processedOrderData.tonAmount = typeof orderData.tonAmount === 'string' ? parseFloat(orderData.tonAmount) || 0 : orderData.tonAmount;
  }
  if (orderData.comment !== undefined) {
    processedOrderData.comment = orderData.comment;
  }

  // PUT to the correct endpoint for updating orders
  const response = await axiosInstance.put(`/orders`, processedOrderData);
  return response.data;
};